import {
    createContext, useEffect, useRef, useState
} from 'react';
import {
    createWebSocketClient
} from 'shared/api/clients/knowledgeBuilder.httpClient';
import {
    useParams
} from 'react-router-dom';
import type {
    FileManagerContextValue,
    FileManagerProviderProps,
    ProcessingStatus
} from './context.types';

const defaultFileManagerContext: FileManagerContextValue = {
    socket: null,
    l1Status: 'idle',
    l2Status: 'idle',
    lastL1Message: null,
    lastL2Message: null
};

export const FileManagerContext = createContext<FileManagerContextValue>(defaultFileManagerContext);

export const FileManagerProvider = ({
    children
}: FileManagerProviderProps) => {
    const {
        oragID, collectionId
    } = useParams();
    const [socket, setSocket] = useState<WebSocket | null>(null);
    const [l1Status, setL1Status] = useState<ProcessingStatus>('idle');
    const [l2Status, setL2Status] = useState<ProcessingStatus>('idle');
    const [lastL1Message, setLastL1Message] = useState<any>(null);
    const [lastL2Message, setLastL2Message] = useState<any>(null);
    const l1PollRef = useRef<WebSocket | null>(null);
    const l2PollRef = useRef<WebSocket | null>(null);
    const l1TimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const l2TimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const createWebSocketConnection = (type: 'l1' | 'l2') => {
            const ws = createWebSocketClient(`/ws/processing_status/${collectionId}?organization_id=${oragID}&processing_type=${type}`);
            return ws;
        };

        const applyL1Message = (message: any) => {
            setLastL1Message(message);
            if (message.status === 'processing') {
                setL1Status('processing');
            } else if (message.status === 'completed') {
                setL1Status('completed');
            } else if (message.status === 'idle') {
                setL1Status('idle');
            } else if (message.status === 'cancelled') {
                setL1Status('cancelled');
            } else if (message.status === 'error') {
                setL1Status('error');
                console.error(`Error: ${message.message}`);
            }
        };

        const applyL2Message = (message: any) => {
            setLastL2Message(message);
            if (message.status === 'processing') {
                setL2Status('processing');
            } else if (message.status === 'completed') {
                setL2Status('completed');
            } else if (message.status === 'idle') {
                setL2Status('idle');
            } else if (message.status === 'cancelled') {
                setL2Status('cancelled');
            } else if (message.status === 'error') {
                setL2Status('error');
                console.error(`L2 Error: ${message.message}`);
            }
        };

        // A status is "terminal" once a run is over. The KB worker closes the
        // socket on completion without a final message, and on reconnect the
        // status can briefly still read "processing" — so we reconnect and keep
        // polling (capped) until it settles, instead of reconnecting only once.
        const TERMINAL = ['idle', 'completed', 'cancelled', 'error'];
        const pollUntilTerminal = (type: 'l1' | 'l2', attempt = 0) => {
            const apply = type === 'l1' ? applyL1Message : applyL2Message;
            const pollRef = type === 'l1' ? l1PollRef : l2PollRef;
            const timerRef = type === 'l1' ? l1TimerRef : l2TimerRef;
            const poll = createWebSocketConnection(type);
            pollRef.current = poll;
            poll.onmessage = (event: MessageEvent<string>) => {
                const message = JSON.parse(event.data);
                apply(message);
                poll.close();
                if (!TERMINAL.includes(message.status) && attempt < 30) {
                    timerRef.current = setTimeout(() => pollUntilTerminal(type, attempt + 1), 2000);
                }
            };
            poll.onerror = () => poll.close();
        };

        // Create WebSocket connections
        const wsL1 = createWebSocketConnection('l1');
        const wsL2 = createWebSocketConnection('l2');
        setSocket(wsL2); // Only set socket to L2 since it's used for L2 processing

        wsL1.onopen = () => {};

        wsL1.onmessage = (event: MessageEvent<string>) => {
            applyL1Message(JSON.parse(event.data));
        };

        wsL1.onerror = (error: Event) => {
            setL1Status('error');
            console.error('WebSocket error:', error);
            wsL1.close();
        };

        wsL1.onclose = () => {
            // The KB worker closes the connection after completing a job without
            // sending a final status message. Poll until the status settles.
            if (l1PollRef.current && l1PollRef.current.readyState !== WebSocket.CLOSED) return;
            pollUntilTerminal('l1');
        };

        // L2 websocket listeners
        wsL2.onopen = () => {};

        wsL2.onmessage = (event: MessageEvent<string>) => {
            applyL2Message(JSON.parse(event.data));
        };

        wsL2.onerror = (error: Event) => {
            setL2Status('error');
            console.error('L2 WebSocket error:', error);
            wsL2.close();
        };

        wsL2.onclose = () => {
            // Same pattern as L1: poll until the status settles.
            if (l2PollRef.current && l2PollRef.current.readyState !== WebSocket.CLOSED) return;
            pollUntilTerminal('l2');
        };

        // Cleanup function to close WebSocket when component unmounts
        return () => {
            if (l1TimerRef.current) clearTimeout(l1TimerRef.current);
            if (l2TimerRef.current) clearTimeout(l2TimerRef.current);
            if (wsL1.readyState === WebSocket.OPEN) wsL1.close();
            if (wsL2.readyState === WebSocket.OPEN) wsL2.close();
            if (l1PollRef.current && l1PollRef.current.readyState === WebSocket.OPEN) l1PollRef.current.close();
            if (l2PollRef.current && l2PollRef.current.readyState === WebSocket.OPEN) l2PollRef.current.close();
        };
    }, [collectionId]);

    return (
        <FileManagerContext.Provider
            value={{
                socket,
                l1Status,
                l2Status,
                lastL1Message,
                lastL2Message
            }}
        >
            {children}
        </FileManagerContext.Provider>
    );
};
